import {Autocomplete, AutocompleteProps, ChipTypeMap, Typography} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {AppInput} from 'components/form/Inputs';
import {SelectOptionsType} from 'global';
import React from 'react';

export interface AppAutoCompleteProps<
    T extends SelectOptionsType,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = true,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
> extends Omit<
        AutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>,
        'renderInput' | 'label' | 'required' | 'validationMessage' | 'isInvalid'
    > {
    label?: string;
    required?: boolean;
    validationMessage?: string;
    isInvalid?: boolean;
    onScrollBottomHandler?: () => void;
    descriptionText?: string;
}

export function AppAutoComplete<
    T extends SelectOptionsType,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = true,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(props: AppAutoCompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>) {
    const {onScrollBottomHandler, descriptionText, ...rest} = props;
    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const {scrollTop, scrollHeight, clientHeight} = event.currentTarget;
        const isBottom = scrollHeight - scrollTop - clientHeight < 5; // allow 5px threshold

        if (isBottom && onScrollBottomHandler) {
            onScrollBottomHandler();
        }
    };
    return (
        <Autocomplete
            {...rest}
            ListboxProps={{
                onScroll: handleScroll,
            }}
            getOptionLabel={option => (option as any).label || ''}
            renderOption={(props, option) => {
                const {key, ...optionProps} = props;
                return (
                    <span
                        key={option.value}
                        style={{display: 'flex', alignItems: 'center', width: '100%'}}
                        {...optionProps}
                    >
                        {(option as any).color && (
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 12,
                                    height: 12,
                                    backgroundColor: (option as any).color,
                                    borderRadius: '50%',
                                    marginRight: 8,
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Typography
                            variant="body2"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                color: (option as any).textColor,
                            }}
                        >
                            {option.label}
                        </Typography>
                    </span>
                );
            }}
            renderInput={params => {
                return (
                    <AppInput
                        {...params}
                        label={rest.label}
                        size={'small'}
                        required={rest.required}
                        helperText={rest.validationMessage}
                        error={rest.isInvalid}
                        descriptionText={descriptionText}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {rest.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                    />
                );
            }}
        />
    );
}

export default AppAutoComplete;
